import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mic, MicOff, Sparkles, Save, X, FileText, Wand2, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * DocumentEditorModal — tenant source-of-truth editor.
 *
 * Two modes via `mode` prop:
 *   • "edit"   — load existing document via GET /documents/{docId}/text,
 *                save via PUT /documents/{docId}/text
 *   • "manual" — start blank, save via POST /documents/manual
 *
 * Features:
 *   • Plain textarea (no rich editor — intentional, per spec).
 *   • Browser-native dictation via webkitSpeechRecognition; falls back to
 *     POST /documents/transcribe (Whisper) where available.
 *   • Optional AI Suggestions checkbox → on-demand POST /documents/ai-polish
 *     showing a side-by-side review. Tenant must explicitly accept and save.
 *   • Atomic save: backend re-ingests inside one transaction-safe path
 *     (snapshots + rollback on failure), so the modal can trust a 200
 *     response means the tenant's RAG view is consistent.
 */
export default function DocumentEditorModal({ open, onClose, mode = "manual", docId = "", initialTitle = "", onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(initialTitle || "");
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");  // for "Revert"
  const [meta, setMeta] = useState(null);
  // v1.0.17 — AI Suggestions checkbox removed; "Polish with AI" is always visible.
  const [polishing, setPolishing] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState("");  // empty when no suggestion shown
  // Dictation state
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  // Load existing document on open (edit mode only).
  useEffect(() => {
    if (!open) {
      // Reset everything when modal closes so subsequent opens start clean.
      setTitle(""); setContent(""); setOriginal(""); setMeta(null);
      setAiSuggestion(""); setListening(false);
      if (recRef.current) { try { recRef.current.stop(); } catch (_) {} recRef.current = null; }
      return;
    }
    if (mode === "edit" && docId) {
      setLoading(true);
      api.get(`/documents/${docId}/text`)
        .then(({ data }) => {
          setTitle(data.title || data.filename || "");
          setContent(data.content || "");
          setOriginal(data.content || "");
          setMeta(data);
        })
        .catch(err => {
          // 401 is handled by the global axios interceptor (impersonation
          // session expiry → toast + redirect). Close the modal here so
          // the user doesn't see an empty editor that looks like data loss.
          const status = err?.response?.status;
          if (status === 401) {
            onClose?.();
            return;
          }
          toast.error(err.response?.data?.detail || "Failed to load document content. Your saved content is safe — please close and retry.");
        })
        .finally(() => setLoading(false));
    } else {
      setTitle(initialTitle || "Untitled document");
      setContent("");
      setOriginal("");
    }
  }, [open, mode, docId, initialTitle, onClose]);

  // v1.0.16 — refresh/close-tab guard while there are unsaved edits.
  useEffect(() => {
    if (!open) return undefined;
    const dirty = content !== original || (mode === "edit" && (meta?.title || "") !== title);
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "You have unsaved changes. Leave without saving?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [open, content, original, title, meta, mode]);

  // ----- Dictation (browser-native, no extra dep) -----
  const startDictation = () => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Rec) {
      toast.error("Dictation not supported in this browser. Try Chrome or Edge.");
      return;
    }
    try {
      const rec = new Rec();
      rec.continuous = true;
      rec.interimResults = false;
      rec.lang = "en-US";
      rec.onresult = (e) => {
        let final = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
        }
        if (final) {
          setContent(prev => (prev ? prev.replace(/\s+$/, "") + " " : "") + final.trim() + " ");
        }
      };
      rec.onerror = (e) => {
        toast.error(`Dictation: ${e.error || "error"}`);
        setListening(false);
      };
      rec.onend = () => setListening(false);
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      toast.error("Could not start dictation");
    }
  };

  const stopDictation = () => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch (_) {}
      recRef.current = null;
    }
    setListening(false);
  };

  // ----- AI polish -----
  const requestPolish = async () => {
    // v1.0.17 — graceful >150k message instead of failing silently.
    if (content.length > CHAR_LIMIT) {
      toast.info(
        "This document is too large to polish in one step.",
        {
          description:
            "You can still save and use it as knowledge, or split it into smaller sections for AI polish.",
          duration: 8000,
        }
      );
      return;
    }
    if (content.trim().length < 20) {
      toast.error("Add at least 20 characters before requesting AI polish.");
      return;
    }
    setPolishing(true);
    setAiSuggestion("");
    try {
      const { data } = await api.post("/documents/ai-polish", { content, title });
      const polished = (data.polished || "").trim();
      if (!polished) throw new Error("Empty response");
      setAiSuggestion(polished);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI polish failed — try again");
    } finally {
      setPolishing(false);
    }
  };

  const acceptSuggestion = () => {
    setContent(aiSuggestion);
    setAiSuggestion("");
    toast.success("Applied AI suggestion — review and save when ready");
  };

  const discardSuggestion = () => setAiSuggestion("");

  // ----- Save -----
  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!content.trim()) { toast.error("Content is required"); return; }
    setSaving(true);
    try {
      let resp;
      if (mode === "edit") {
        resp = await api.put(`/documents/${docId}/text`, { content, title });
      } else {
        resp = await api.post("/documents/manual", { content, title });
      }
      toast.success(mode === "edit" ? "Saved — Sales Assistant is using the updated content" : "Created — Sales Assistant is indexing the new document");
      onSaved?.(resp.data);
      onClose();
    } catch (err) {
      // The global axios interceptor handles impersonation-expiry 401s by
      // restoring the super-admin token + redirecting; suppress the local
      // "Invalid token" toast in that case so the user sees one clear
      // message, not two conflicting ones.
      if (err?.response?.status !== 401) {
        toast.error(err.response?.data?.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (original && original !== content) {
      setContent(original);
      setAiSuggestion("");
      toast.success("Reverted to last saved version");
    }
  };

  if (!open) return null;

  const dirty = content !== original || (mode === "edit" && (meta?.title || "") !== title);
  const charCount = content.length;
  const CHAR_LIMIT = 150_000;

  // v1.0.16 — Unsaved-changes guard for the modal. We can't hand this to the
  // shared `useUnsavedChangesGuard` hook (it operates on routes) so we inline
  // a `beforeunload` listener for refresh/close + wrap `onClose` so the X /
  // Cancel / backdrop-click paths all confirm before discarding.
  const guardedClose = () => {
    if (dirty && !saving) {
      // eslint-disable-next-line no-alert
      if (!window.confirm("You have unsaved changes. Leave without saving?")) return;
    }
    onClose?.();
  };

  return (
    <div
      data-testid="document-editor-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) guardedClose(); }}
    >
      <div className="bg-background w-full max-w-5xl max-h-[92vh] rounded-xl shadow-2xl flex flex-col overflow-hidden border">
        {/* Header */}
        <div className="px-5 py-3.5 border-b flex items-center justify-between gap-3 bg-card">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-accent shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-sm">{mode === "edit" ? "Edit document" : "Create document"}</div>
              <div className="text-xs text-muted-foreground truncate">
                {mode === "edit"
                  ? "Edits re-index immediately into your Sales Assistant"
                  : "Type or dictate. AI suggestions are optional."}
                {meta?.source_type && <span className="ml-2 px-1.5 py-0.5 rounded bg-muted text-[10px] uppercase tracking-wider">{meta.source_type}</span>}
              </div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={guardedClose} disabled={saving} data-testid="doc-editor-close" aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 grid place-items-center py-24 text-muted-foreground gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading document…
          </div>
        ) : (
          <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
            <div>
              <Label htmlFor="doc-editor-title" className="text-xs uppercase tracking-wide text-muted-foreground">Title</Label>
              <Input
                id="doc-editor-title"
                data-testid="doc-editor-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Service hours and policies"
                className="mt-1"
                disabled={saving}
              />
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-y py-2.5 -mx-5 px-5 bg-muted/30">
              <Button
                size="sm"
                variant={listening ? "default" : "outline"}
                onClick={listening ? stopDictation : startDictation}
                disabled={saving}
                data-testid="doc-editor-dictate"
              >
                {listening ? <><MicOff className="w-3.5 h-3.5 mr-1.5" />Stop dictation</> : <><Mic className="w-3.5 h-3.5 mr-1.5" />Dictate</>}
              </Button>
              <div className="flex items-center gap-2 ml-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={requestPolish}
                  disabled={polishing || saving || content.trim().length < 20 || content.length > CHAR_LIMIT}
                  data-testid="doc-editor-polish"
                  title={content.length > CHAR_LIMIT
                    ? "This document is too large to polish in one step."
                    : "Polish with AI"}
                >
                  {polishing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Polishing…</> : <><Wand2 className="w-3.5 h-3.5 mr-1.5" />Polish with AI</>}
                </Button>
              </div>
              {mode === "edit" && original && content !== original && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={revert}
                  disabled={saving}
                  data-testid="doc-editor-revert"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Revert
                </Button>
              )}
              <div className="ml-auto text-xs text-muted-foreground">
                {charCount.toLocaleString()} / {CHAR_LIMIT.toLocaleString()} characters
              </div>
            </div>

            {/* Editor + side-by-side AI suggestion when present */}
            <div className={aiSuggestion ? "grid grid-cols-1 lg:grid-cols-2 gap-3" : ""}>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  {aiSuggestion ? "Your text" : "Content"}
                </Label>
                <textarea
                  data-testid="doc-editor-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={mode === "manual"
                    ? "Type or dictate the content your Sales Assistant should know. For example: services, pricing, hours, policies, FAQs…"
                    : "Edit the extracted text. Saving re-indexes the document immediately."}
                  className="w-full mt-1 px-3 py-2.5 border rounded-md text-sm font-mono leading-relaxed bg-background focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                  rows={aiSuggestion ? 18 : 16}
                  disabled={saving}
                  maxLength={CHAR_LIMIT}
                />
              </div>
              {aiSuggestion && (
                <div data-testid="doc-editor-ai-preview">
                  <div className="flex items-center justify-between mt-0">
                    <Label className="text-xs uppercase tracking-wide text-accent flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> AI suggested
                    </Label>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="ghost" onClick={discardSuggestion} data-testid="doc-editor-ai-discard">Discard</Button>
                      <Button size="sm" onClick={acceptSuggestion} data-testid="doc-editor-ai-accept">Use suggestion</Button>
                    </div>
                  </div>
                  <div className="w-full mt-1 px-3 py-2.5 border rounded-md text-sm font-mono leading-relaxed bg-accent/5 overflow-auto whitespace-pre-wrap" style={{ minHeight: 18 * 20, maxHeight: "60vh" }}>
                    {aiSuggestion}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground italic">
                    Review carefully — AI may rephrase. Nothing is saved until you click "Save".
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3.5 border-t flex items-center justify-between gap-3 bg-card">
          <div className="text-xs text-muted-foreground">
            {dirty ? <span className="text-amber-700">Unsaved changes — Sales Assistant will re-index on save.</span> : <span>Up to date.</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={guardedClose} disabled={saving} data-testid="doc-editor-cancel">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || loading || !title.trim() || !content.trim()}
              data-testid="doc-editor-save"
            >
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</> : <><Save className="w-3.5 h-3.5 mr-1.5" />{mode === "edit" ? "Save changes" : "Create document"}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
